import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { BarChart3, BookOpen, Boxes, FlaskConical, KeyRound, LayoutDashboard, LogOut, Menu, PlayCircle, ScrollText, Settings, Wallet, Webhook, X } from "lucide-react";
import Logo from "./Logo";
import consoleApi from "../lib/apiConsole";

const NAV = [
  { to: "/console", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/console/keys", label: "API Keys", icon: KeyRound },
  { to: "/console/docs", label: "Documentation", icon: BookOpen },
  { to: "/console/wallet", label: "Wallet", icon: Wallet },
  { to: "/console/shipments", label: "Shipments", icon: Boxes },
  { to: "/console/reports", label: "API Reports", icon: BarChart3 },
  { to: "/console/logs", label: "API Logs", icon: ScrollText },
  { to: "/console/webhooks", label: "Webhooks", icon: Webhook },
  { to: "/console/sandbox", label: "Sandbox", icon: FlaskConical },
  { to: "/console/playground", label: "API Playground", icon: PlayCircle },
  { to: "/console/settings", label: "Settings", icon: Settings },
];

export default function ApiConsoleLayout() {
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    consoleApi.get("/api-console/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => navigate("/console/login", { replace: true }));
  }, [navigate, location.pathname]);

  const logout = async () => {
    await consoleApi.post("/api-console/auth/logout").catch(() => {});
    navigate("/console/login", { replace: true });
  };

  const nav = (onNavigate) => (
    <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-6" data-testid="api-console-sidebar">
      {NAV.map((item) => (
        <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate} data-testid={`api-console-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#0757D9] text-white shadow-lg shadow-blue-950/30" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
          <item.icon className="h-4 w-4" /> {item.label}
        </NavLink>
      ))}
    </nav>
  );

  if (!user) return <div className="flex min-h-screen items-center justify-center bg-[#071B3A] text-cyan-300" data-testid="api-console-loading">Loading API Console…</div>;

  return (
    <div className="min-h-screen bg-[#071B3A] text-white" data-testid="api-console-layout">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-[#1E3A66] bg-[#071B3A] lg:flex">
        <div className="px-5 py-5"><Logo dark /><p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300">API Console</p></div>
        {nav()}
        <div className="border-t border-[#1E3A66] p-4">
          <button onClick={logout} data-testid="api-console-logout-btn" className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-400 transition-colors hover:bg-white/5 hover:text-white"><LogOut className="h-4 w-4" /> Logout</button>
        </div>
      </aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-[#071B3A]"><div className="flex items-center justify-between p-5"><Logo dark /><button data-testid="api-console-mobile-close" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button></div>{nav(() => setOpen(false))}</aside>
        </div>
      )}
      <div className="min-w-0 lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-[#1E3A66] bg-[#071B3A]/90 px-5 py-4 backdrop-blur-xl">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="api-console-mobile-menu"><Menu className="h-5 w-5" /></button>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Environment</p>
            <div className="mt-1 flex gap-2"><span className="rounded-md border border-purple-500/30 bg-purple-500/15 px-2 py-0.5 text-[10px] font-bold text-purple-300">SANDBOX</span>{user.production_enabled && <span className="rounded-md border border-blue-500/30 bg-blue-500/15 px-2 py-0.5 text-[10px] font-bold text-blue-300">LIVE ENABLED</span>}</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block"><p className="text-sm font-semibold" data-testid="api-console-company-name">{user.company_name}</p><p className="text-xs text-slate-500">{user.email}</p></div>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-400 font-display font-bold text-[#071B3A]">{(user.company_name || "A")[0]}</span>
          </div>
        </header>
        <main className="p-5 sm:p-8"><Outlet context={{ apiUser: user }} /></main>
      </div>
    </div>
  );
}
